import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {styled} from 'styled-components';

import {getDoorCadManifest} from '@shared/models/cad/manifest_registry';

import {Flex} from '@shared_web/components/core2/flex';

import {Door3dViewer} from '@mavismart_frontend/components/door_3d/door_3d_viewer';
import {Loader} from '@mavismart_frontend/components/loader';
import {Translate} from '@mavismart_frontend/components/translate';
import {TEXT_MAIN_COLOR} from '@mavismart_frontend/lib/constants';
import {fetchDoorCadModel, useDoorCadModel} from '@mavismart_frontend/stores/door_cad_store';

interface Door3dProps {
  doorId: string;
}

/**
 * "3D" tab of the door detail page.
 *
 * This tab is only mounted when the user opens it (see `NavigationTabs`), which is what defers both
 * the request for the model URL and the ~3 MB GLB download. The URL is short-lived, so asking for it
 * any earlier would risk handing the viewer an expired one.
 */
export const Door3d: FC<Door3dProps> = ({doorId}) => {
  const model = useDoorCadModel(doorId);
  const [selectedPartId, setSelectedPartId] = useState<string | undefined>();
  const [viewerError, setViewerError] = useState<string | undefined>();

  useEffect(() => {
    fetchDoorCadModel(doorId);
  }, [doorId]);

  // A different door means a different model: drop the selection rather than keep a stale part id.
  // biome-ignore lint/correctness/useExhaustiveDependencies: resetting *because* doorId changed
  useEffect(() => {
    setSelectedPartId(undefined);
    setViewerError(undefined);
  }, [doorId]);

  const manifest = useMemo(
    () => (model?.kind === 'model' ? getDoorCadManifest(model.modelKey) : undefined),
    [model]
  );

  const handleError = useCallback((error: Error) => setViewerError(error.message), []);

  if (model === undefined) {
    return <Loader />;
  }

  if (model.kind === 'none') {
    return (
      <Centered>
        <Message>
          <Translate value="door_3d__no_model" />
        </Message>
      </Centered>
    );
  }

  if (model.kind === 'error') {
    return (
      <Centered role="alert">
        <Message>
          <Translate value="door_3d__model_unavailable" />
        </Message>
        <Detail>{model.message}</Detail>
      </Centered>
    );
  }

  return (
    <Wrapper $column $gap="s">
      <Door3dViewer
        modelUrl={model.modelUrl}
        manifest={manifest}
        selectedPartId={selectedPartId}
        onSelectPart={setSelectedPartId}
        onError={handleError}
      />

      {viewerError !== undefined && <Detail role="alert">{viewerError}</Detail>}
    </Wrapper>
  );
};

Door3d.displayName = 'Door3d';

const Wrapper = styled(Flex)`
  width: 100%;
  height: 100%;
  min-height: 0;
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-grow: 1;
  height: 100%;
  padding: 20px;
  text-align: center;
`;

const Message = styled.div`
  color: ${TEXT_MAIN_COLOR};
`;

const Detail = styled.div`
  font-size: 12px;
  color: ${TEXT_MAIN_COLOR}aa;
  overflow-wrap: anywhere;
  max-width: 480px;
`;
